import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Filtro de listagem que vive na barra de endereco, nao no estado do componente.
 *
 * A razao e de uso, nao de arquitetura: "me manda a lista de condutores com CRMC
 * vencido" precisa ser um link que a outra pessoa abre e ve a mesma coisa. Estado
 * em `useState` nao se cola no chat, nao entra em favorito e se perde ao recarregar
 * a pagina.
 *
 * Duas decisoes que evitam endereco poluido:
 *
 * 1. VALOR IGUAL AO PADRAO NAO ENTRA NA URL. `?status=` e `?page=1` nao dizem nada
 *    que a ausencia deles ja nao diga, e ficariam grudados no endereco depois de o
 *    usuario voltar o filtro ao normal.
 * 2. TROCAR FILTRO VOLTA PARA A PRIMEIRA PAGINA. Ficar na pagina 7 de um resultado
 *    que agora tem duas paginas mostra tabela vazia sem explicacao — parece defeito,
 *    e e so paginacao fora de faixa.
 *
 * `replace: true` em tudo: filtrar nao e navegar. Sem isso, cada tecla digitada na
 * busca empilharia uma entrada no historico, e o botao "voltar" do navegador
 * gastaria dezenas de cliques para sair da tela.
 */

const POR_PAGINA_PADRAO = 10;

export interface FiltrosDaUrl<T extends Record<string, string>> {
  /** Valor atual de cada filtro declarado, sempre com o padrao como piso. */
  values: T;
  /**
   * Grava um filtro. Volta para a primeira pagina.
   *
   * A chave e `string`, e nao `keyof T`, DE PROPOSITO: este gancho alimenta a `FilterBar`,
   * que e generica e entrega `(key: string, value: string)` — ela recebe a lista de campos
   * em tempo de execucao e nao tem como conhecer as chaves de cada tela. Estreitar aqui
   * obrigaria cada uma das telas a converter o tipo na passagem, e cinco conversoes
   * espalhadas escondem mais erro do que a assinatura estreita evitaria. Chave que nao
   * existe em `padroes` simplesmente entra no endereco e nao e lida por ninguem.
   */
  setFilter: (chave: string, valor: string) => void;
  /** Grava varios de uma vez — usado pelos atalhos de filtro. */
  setMany: (mudancas: Record<string, string>) => void;
  /** Limpa todo o endereco, inclusive pagina e tamanho de pagina. */
  clearAll: () => void;
  page: number;
  setPage: (pagina: number) => void;
  perPage: number;
  setPerPage: (porPagina: number) => void;
}

export function useUrlFilters<T extends Record<string, string>>(padroes: T): FiltrosDaUrl<T> {
  const [parametros, definirParametros] = useSearchParams();

  /**
   * `padroes` chega como objeto literal — referencia nova a cada render. Sem esta
   * ancora, todo `useCallback` abaixo se recriaria a cada render e levaria consigo
   * qualquer `useEffect`/`useMemo` que dependa deles nas telas.
   */
  const assinaturaDosPadroes = JSON.stringify(padroes);
  const padroesEstaveis = useMemo(
    () => JSON.parse(assinaturaDosPadroes) as T,
    [assinaturaDosPadroes],
  );

  const values = useMemo(() => {
    const atuais = {} as Record<string, string>;
    for (const chave of Object.keys(padroesEstaveis)) {
      atuais[chave] = parametros.get(chave) || padroesEstaveis[chave] || '';
    }
    return atuais as T;
  }, [parametros, padroesEstaveis]);

  const setFilter = useCallback(
    (chave: string, valor: string) => {
      definirParametros(
        (anteriores) => {
          const proximos = new URLSearchParams(anteriores);
          if (valor && valor !== padroesEstaveis[chave]) {
            proximos.set(chave, valor);
          } else {
            proximos.delete(chave);
          }
          if (chave !== 'page') {
            proximos.delete('page');
          }
          return proximos;
        },
        { replace: true },
      );
    },
    [definirParametros, padroesEstaveis],
  );

  const setMany = useCallback(
    (mudancas: Record<string, string>) => {
      definirParametros(
        (anteriores) => {
          const proximos = new URLSearchParams(anteriores);
          for (const [chave, valor] of Object.entries(mudancas)) {
            const texto = valor ?? '';
            if (texto && texto !== padroesEstaveis[chave]) {
              proximos.set(chave, texto);
            } else {
              proximos.delete(chave);
            }
          }
          proximos.delete('page');
          return proximos;
        },
        { replace: true },
      );
    },
    [definirParametros, padroesEstaveis],
  );

  const clearAll = useCallback(() => {
    definirParametros({}, { replace: true });
  }, [definirParametros]);

  const page = Math.max(1, Number.parseInt(parametros.get('page') || '1', 10) || 1);

  const setPage = useCallback(
    (pagina: number) => {
      definirParametros(
        (anteriores) => {
          const proximos = new URLSearchParams(anteriores);
          if (pagina > 1) {
            proximos.set('page', String(pagina));
          } else {
            proximos.delete('page');
          }
          return proximos;
        },
        { replace: true },
      );
    },
    [definirParametros],
  );

  const perPage =
    Number.parseInt(parametros.get('perPage') || String(POR_PAGINA_PADRAO), 10) ||
    POR_PAGINA_PADRAO;

  const setPerPage = useCallback(
    (porPagina: number) => {
      definirParametros(
        (anteriores) => {
          const proximos = new URLSearchParams(anteriores);
          if (porPagina !== POR_PAGINA_PADRAO) {
            proximos.set('perPage', String(porPagina));
          } else {
            proximos.delete('perPage');
          }
          proximos.delete('page');
          return proximos;
        },
        { replace: true },
      );
    },
    [definirParametros],
  );

  return { values, setFilter, setMany, clearAll, page, setPage, perPage, setPerPage };
}
